/**
 * 统一历史记录组件
 * 整合任务字段变更和工作流操作记录
 */

import {
  CheckCircle,
  XCircle,
  Clock,
  Play,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  GitBranch,
  UserPlus,
  Edit3,
  User,
  Flag,
  Calendar,
  Tag,
  AlertCircle,
} from 'lucide-react'
import { useState, useMemo } from 'react'

import { cn } from '@/lib/utils'

// ==================== 类型定义 ====================

export interface IssueHistoryRecord {
  id: string
  issueId: string
  userId: string
  field: string
  oldValue: string | null
  newValue: string | null
  createdAt: string
}

export interface WorkflowHistoryRecord {
  id: string
  action: string
  fromStage: string | null
  toStage: string | null
  userId: string
  actorName: string | null
  trackType: string | null
  trackAssigneeId: string | null
  trackAssigneeName: string | null
  reason: string | null
  metadata: string | null
  createdAt: string
}

// 统一的历史记录条目
interface UnifiedHistoryEntry {
  id: string
  type: 'issue' | 'workflow'
  timestamp: Date
  createdAt: string
  // 任务变更字段
  field?: string
  oldValue?: string | null
  newValue?: string | null
  // 工作流字段
  action?: string
  fromStage?: string | null
  toStage?: string | null
  reason?: string | null
  actorName?: string | null
  trackType?: string | null
  trackAssigneeName?: string | null
}

interface UnifiedHistoryProps {
  issueHistory?: IssueHistoryRecord[]
  workflowHistory?: WorkflowHistoryRecord[]
  isLoading?: boolean
  maxVisible?: number
  className?: string
}

// ==================== 组件实现 ====================

export function UnifiedHistory({
  issueHistory = [],
  workflowHistory = [],
  isLoading = false,
  maxVisible = 10,
  className,
}: UnifiedHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set())

  // 合并并按时间排序
  const mergedHistory = useMemo(() => {
    const entries: UnifiedHistoryEntry[] = []

    // 添加任务历史
    issueHistory.forEach((record) => {
      entries.push({
        id: `issue-${record.id}`,
        type: 'issue',
        timestamp: new Date(record.createdAt),
        createdAt: record.createdAt,
        field: record.field,
        oldValue: record.oldValue,
        newValue: record.newValue,
      })
    })

    // 添加工作流历史
    workflowHistory.forEach((record) => {
      entries.push({
        id: `workflow-${record.id}`,
        type: 'workflow',
        timestamp: new Date(record.createdAt),
        createdAt: record.createdAt,
        action: record.action,
        fromStage: record.fromStage,
        toStage: record.toStage,
        reason: record.reason,
        actorName: record.actorName,
        trackType: record.trackType,
        trackAssigneeName: record.trackAssigneeName,
      })
    })

    // 按时间降序排序（最新的在前）
    return entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  }, [issueHistory, workflowHistory])

  // 根据展开状态显示的记录
  const visibleHistory = useMemo(() => {
    if (isExpanded || mergedHistory.length <= maxVisible) {
      return mergedHistory
    }
    return mergedHistory.slice(0, maxVisible)
  }, [mergedHistory, maxVisible, isExpanded])

  const hasMore = mergedHistory.length > maxVisible

  const toggleReasonExpanded = (id: string) => {
    setExpandedReasons((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className={cn('text-muted-foreground py-8 text-center text-sm', className)}>
        加载中...
      </div>
    )
  }

  if (mergedHistory.length === 0) {
    return (
      <div className={cn('text-muted-foreground py-8 text-center text-sm', className)}>
        暂无历史记录
      </div>
    )
  }

  return (
    <div className={className}>
      {/* 时间线容器 */}
      <div className="relative">
        {/* 垂直连接线 */}
        <div className="bg-border absolute bottom-2 left-[11px] top-2 w-0.5" />

        {/* 历史记录列表 */}
        <div className="space-y-4">
          {visibleHistory.map((entry) => (
            <HistoryEntry
              key={entry.id}
              entry={entry}
              isReasonExpanded={expandedReasons.has(entry.id)}
              onToggleReason={() => toggleReasonExpanded(entry.id)}
            />
          ))}
        </div>
      </div>

      {/* 展开/收起按钮 */}
      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-muted-foreground hover:bg-muted/50 hover:text-foreground mt-4 flex w-full items-center justify-center gap-1 rounded-md py-2 text-sm transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              收起历史记录
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              查看更多 ({mergedHistory.length - maxVisible} 条)
            </>
          )}
        </button>
      )}
    </div>
  )
}

// ==================== 单条历史记录 ====================

interface HistoryEntryProps {
  entry: UnifiedHistoryEntry
  isReasonExpanded: boolean
  onToggleReason: () => void
}

function HistoryEntry({ entry, isReasonExpanded, onToggleReason }: HistoryEntryProps) {
  const { icon, iconColor, bgColor } = getEntryDisplay(entry)

  return (
    <div className="relative flex gap-3">
      {/* 图标节点 */}
      <div
        className={cn(
          'relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          bgColor
        )}
      >
        <span className={cn('h-4 w-4', iconColor)}>{icon}</span>
      </div>

      {/* 内容区域 */}
      <div className="min-w-0 flex-1 pb-1">
        {/* 任务字段变更 */}
        {entry.type === 'issue' && (
          <IssueHistoryContent entry={entry} />
        )}

        {/* 工作流操作 */}
        {entry.type === 'workflow' && (
          <WorkflowHistoryContent
            entry={entry}
            isReasonExpanded={isReasonExpanded}
            onToggleReason={onToggleReason}
          />
        )}

        {/* 时间戳 - 显示完整系统时间 */}
        <p className="text-muted-foreground mt-1.5 text-xs">{formatFullTimestamp(entry.createdAt)}</p>
      </div>
    </div>
  )
}

// ==================== 任务字段变更内容 ====================

function IssueHistoryContent({ entry }: { entry: UnifiedHistoryEntry }) {
  const fieldLabel = getFieldLabel(entry.field!)
  const oldVal = formatFieldValue(entry.field!, entry.oldValue)
  const newVal = formatFieldValue(entry.field!, entry.newValue)

  return (
    <>
      <div className="text-sm font-medium">{fieldLabel}变更</div>
      <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-sm">
        <span className="bg-muted rounded px-1.5 py-0.5 line-through opacity-70">
          {oldVal || '(空)'}
        </span>
        <ArrowRight className="h-3 w-3 shrink-0" />
        <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 font-medium">
          {newVal || '(空)'}
        </span>
      </div>
    </>
  )
}

// ==================== 工作流操作内容 ====================

function WorkflowHistoryContent({
  entry,
  isReasonExpanded,
  onToggleReason,
}: {
  entry: UnifiedHistoryEntry
  isReasonExpanded: boolean
  onToggleReason: () => void
}) {
  const detail = getWorkflowDetailDisplay(entry)

  return (
    <>
      {/* 主标题 */}
      <div className="text-sm font-medium">{detail.title}</div>

      {/* 详细描述 */}
      {detail.description && (
        <div className="text-foreground mt-1 text-sm">{detail.description}</div>
      )}

      {/* 阶段流转信息 */}
      {detail.stageTransition && (
        <div className="bg-muted/50 mt-2 rounded-md p-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="bg-muted text-muted-foreground rounded px-2 py-0.5">
              {detail.stageTransition.from}
            </span>
            <ArrowRight className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="bg-primary/10 text-primary rounded px-2 py-0.5 font-medium">
              {detail.stageTransition.to}
            </span>
          </div>
        </div>
      )}

      {/* 轨道信息 */}
      {detail.trackInfo && (
        <div className="mt-2 flex items-center gap-1.5 text-sm">
          <GitBranch className="text-muted-foreground h-4 w-4" />
          <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', detail.trackInfo.color)}>
            {detail.trackInfo.label}
          </span>
        </div>
      )}

      {/* 操作人 */}
      {entry.actorName && (
        <div className="text-muted-foreground mt-1.5 flex items-center gap-1 text-xs">
          <User className="h-3 w-3" />
          操作人：{entry.actorName}
        </div>
      )}

      {/* 原因（可展开） */}
      {entry.reason && (
        <div className="mt-2">
          <button
            onClick={onToggleReason}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
          >
            {isReasonExpanded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {isReasonExpanded ? '收起原因' : '查看原因'}
          </button>
          {isReasonExpanded && (
            <div className="bg-muted/50 text-muted-foreground mt-1 rounded-md p-2 text-sm">
              {entry.reason}
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ==================== 工作流详细显示 ====================

interface WorkflowDetailDisplay {
  title: string
  description?: string
  stageTransition?: {
    from: string
    to: string
  }
  trackInfo?: {
    label: string
    color: string
  }
}

function getWorkflowDetailDisplay(entry: UnifiedHistoryEntry): WorkflowDetailDisplay {
  const action = entry.action!
  const fromStage = entry.fromStage ? getStageLabel(entry.fromStage) : null
  const toStage = entry.toStage ? getStageLabel(entry.toStage) : null
  const trackType = entry.trackType
  const trackLabel = trackType ? getTrackLabel(trackType) : null
  const trackColor = trackType === 'frontend'
    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
  const assigneeName = entry.trackAssigneeName

  switch (action) {
    // 阶段提交审核
    case 'submit_review':
      return {
        title: '提交阶段审核',
        description: fromStage
          ? `「${fromStage}」阶段已完成，提交审核`
          : '当前阶段已完成，提交审核',
        stageTransition: fromStage ? { from: fromStage, to: '待审核' } : undefined,
      }

    // 阶段审批通过
    case 'approve':
      return {
        title: '阶段审核通过',
        description: fromStage && toStage
          ? `「${fromStage}」审核通过，进入「${toStage}」阶段`
          : toStage
            ? `审核通过，进入「${toStage}」阶段`
            : fromStage
              ? `「${fromStage}」审核通过`
              : '阶段审核通过',
        stageTransition: fromStage && toStage ? { from: fromStage, to: toStage } : undefined,
      }

    // 阶段流转（自动或手动）
    case 'stage_transition':
      return {
        title: '阶段流转',
        description: fromStage && toStage
          ? `任务从「${fromStage}」流转到「${toStage}」`
          : toStage
            ? `任务进入「${toStage}」阶段`
            : fromStage
              ? `任务离开「${fromStage}」阶段`
              : '工作流阶段变更',
        stageTransition: fromStage && toStage ? { from: fromStage, to: toStage } : undefined,
      }

    // 阶段审批拒绝
    case 'reject':
      return {
        title: '阶段审核拒绝',
        description: fromStage && toStage
          ? `「${fromStage}」审核未通过，回退到「${toStage}」`
          : toStage
            ? `审核未通过，回退到「${toStage}」阶段`
            : '审核未通过',
        stageTransition: fromStage && toStage ? { from: fromStage, to: toStage } : undefined,
      }

    // 回退操作
    case 'rollback':
      return {
        title: '工作流回退',
        description: fromStage && toStage
          ? `任务从「${fromStage}」回退到「${toStage}」重新处理`
          : toStage
            ? `任务回退到「${toStage}」阶段`
            : '任务已回退',
        stageTransition: fromStage && toStage ? { from: fromStage, to: toStage } : undefined,
      }

    // 分配参与人
    case 'participant_assigned':
    case 'add_stage_participants':
    case 'set_stage_reviewer':
      return {
        title: '分配阶段人员',
        description: toStage
          ? `为「${toStage}」阶段分配了参与人员`
          : fromStage
            ? `为「${fromStage}」阶段分配了参与人员`
            : '已分配阶段参与人员',
      }

    // 轨道开始开发
    case 'start_track':
    case 'track_started':
      return {
        title: `${trackLabel || '开发轨道'}启动`,
        description: assigneeName
          ? `${trackLabel || '开发轨道'}已启动，负责人：${assigneeName}`
          : `${trackLabel || '开发轨道'}已启动，进入开发阶段`,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 轨道提交审查
    case 'submit_track_review':
    case 'track_submitted':
      return {
        title: `${trackLabel || '开发轨道'}提交审查`,
        description: `${trackLabel || '开发轨道'}开发完成，已提交代码审查`,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 轨道审查通过
    case 'approve_track':
    case 'track_approved':
      return {
        title: `${trackLabel || '开发轨道'}审查通过`,
        description: `${trackLabel || '开发轨道'}代码审查通过，等待其他轨道完成`,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 轨道审查拒绝
    case 'reject_track':
    case 'track_rejected':
      return {
        title: `${trackLabel || '开发轨道'}审查拒绝`,
        description: `${trackLabel || '开发轨道'}代码审查未通过，已打回修改`,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 轨道回退
    case 'track_rollback':
      return {
        title: `${trackLabel || '开发轨道'}回退`,
        description: `${trackLabel || '开发轨道'}已回退到开发阶段`,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 更新轨道负责人
    case 'update_track_assignee':
      return {
        title: `${trackLabel || '开发轨道'}负责人变更`,
        description: assigneeName
          ? `${trackLabel || '开发轨道'}负责人已更新为：${assigneeName}`
          : `${trackLabel || '开发轨道'}负责人已更新`,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 从测试回退到开发轨道
    case 'rollback_from_testing':
      return {
        title: '从测试阶段回退',
        description: trackLabel
          ? `测试未通过，${trackLabel}已回退到开发阶段`
          : '测试未通过，开发轨道已回退',
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }

    // 轨道汇合（所有轨道完成）
    case 'tracks_converged':
      return {
        title: '开发轨道汇合',
        description: '所有开发轨道已完成代码审查，准备进入测试阶段',
      }

    // 工作流初始化
    case 'workflow_initialized':
    case 'initialize':
      return {
        title: '工作流初始化',
        description: '任务工作流已初始化，开始流程追踪',
      }

    // 工作流完成
    case 'workflow_completed':
    case 'complete':
      return {
        title: '工作流完成',
        description: '任务已完成所有工作流阶段',
      }

    default:
      return {
        title: action,
        description: fromStage && toStage ? `${fromStage} → ${toStage}` : undefined,
        stageTransition: fromStage && toStage ? { from: fromStage, to: toStage } : undefined,
        trackInfo: trackLabel ? { label: trackLabel, color: trackColor } : undefined,
      }
  }
}

// ==================== 辅助函数 ====================

function getEntryDisplay(entry: UnifiedHistoryEntry): {
  icon: React.ReactNode
  iconColor: string
  bgColor: string
  label: string
} {
  if (entry.type === 'issue') {
    return getFieldChangeDisplay(entry.field!)
  }
  return getWorkflowActionDisplay(entry.action!)
}

function getFieldChangeDisplay(field: string): {
  icon: React.ReactNode
  iconColor: string
  bgColor: string
  label: string
} {
  switch (field) {
    case 'status':
      return {
        icon: <CheckCircle className="h-4 w-4" />,
        iconColor: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        label: '状态变更',
      }
    case 'assigneeId':
    case 'assignee':
      return {
        icon: <User className="h-4 w-4" />,
        iconColor: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        label: '负责人变更',
      }
    case 'priority':
      return {
        icon: <Flag className="h-4 w-4" />,
        iconColor: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        label: '优先级变更',
      }
    case 'dueDate':
      return {
        icon: <Calendar className="h-4 w-4" />,
        iconColor: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: '截止日期变更',
      }
    case 'title':
      return {
        icon: <Edit3 className="h-4 w-4" />,
        iconColor: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        label: '标题变更',
      }
    case 'description':
      return {
        icon: <Edit3 className="h-4 w-4" />,
        iconColor: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        label: '描述变更',
      }
    case 'type':
      return {
        icon: <Tag className="h-4 w-4" />,
        iconColor: 'text-teal-600 dark:text-teal-400',
        bgColor: 'bg-teal-100 dark:bg-teal-900/30',
        label: '类型变更',
      }
    case 'blockedReason':
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        iconColor: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        label: '阻塞原因变更',
      }
    default:
      return {
        icon: <Edit3 className="h-4 w-4" />,
        iconColor: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        label: `${getFieldLabel(field)}变更`,
      }
  }
}

function getWorkflowActionDisplay(action: string): {
  icon: React.ReactNode
  iconColor: string
  bgColor: string
  label: string
} {
  switch (action) {
    // 阶段审批通过
    case 'approve':
    case 'stage_transition':
      return {
        icon: <CheckCircle className="h-4 w-4" />,
        iconColor: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: action === 'approve' ? '审批通过' : '阶段流转',
      }
    // 阶段拒绝/回退
    case 'reject':
    case 'rollback':
    case 'rollback_from_testing':
      return {
        icon: <XCircle className="h-4 w-4" />,
        iconColor: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        label: action === 'reject' ? '审批拒绝' : '回退',
      }
    // 提交审核
    case 'submit_review':
      return {
        icon: <Play className="h-4 w-4" />,
        iconColor: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        label: '提交审核',
      }
    // 分配参与人
    case 'participant_assigned':
      return {
        icon: <UserPlus className="h-4 w-4" />,
        iconColor: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        label: '分配参与人员',
      }
    // 轨道启动
    case 'start_track':
    case 'track_started':
      return {
        icon: <GitBranch className="h-4 w-4" />,
        iconColor: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        label: '轨道启动',
      }
    // 轨道提交审查
    case 'submit_track_review':
    case 'track_submitted':
      return {
        icon: <GitBranch className="h-4 w-4" />,
        iconColor: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        label: '提交代码审查',
      }
    // 轨道审查通过
    case 'approve_track':
    case 'track_approved':
      return {
        icon: <GitBranch className="h-4 w-4" />,
        iconColor: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: '代码审查通过',
      }
    // 轨道审查拒绝
    case 'reject_track':
    case 'track_rejected':
      return {
        icon: <GitBranch className="h-4 w-4" />,
        iconColor: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        label: '代码审查拒绝',
      }
    // 轨道负责人变更
    case 'update_track_assignee':
      return {
        icon: <UserPlus className="h-4 w-4" />,
        iconColor: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        label: '轨道负责人变更',
      }
    // 轨道汇合
    case 'tracks_converged':
      return {
        icon: <CheckCircle className="h-4 w-4" />,
        iconColor: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: '轨道汇合',
      }
    // 工作流初始化
    case 'workflow_initialized':
    case 'initialize':
      return {
        icon: <Play className="h-4 w-4" />,
        iconColor: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        label: '工作流初始化',
      }
    // 工作流完成
    case 'workflow_completed':
    case 'complete':
      return {
        icon: <CheckCircle className="h-4 w-4" />,
        iconColor: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        label: '工作流完成',
      }
    default:
      return {
        icon: <Clock className="h-4 w-4" />,
        iconColor: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        label: action,
      }
  }
}

function getFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    status: '状态',
    assigneeId: '负责人',
    assignee: '负责人',
    priority: '优先级',
    dueDate: '截止日期',
    title: '标题',
    description: '描述',
    type: '类型',
    estimatedHours: '预估工时',
    actualHours: '实际工时',
    blockedReason: '阻塞原因',
    developerGroup: '开发组',
  }
  return labels[field] ?? field
}

function formatFieldValue(field: string, value: string | null | undefined): string {
  if (!value) return ''

  // 状态值映射
  if (field === 'status') {
    const statusLabels: Record<string, string> = {
      backlog: '待办',
      todo: '计划中',
      in_progress: '进行中',
      code_review: '代码审查',
      testing: '测试中',
      done: '已完成',
      blocked: '已阻塞',
      cancelled: '已取消',
    }
    return statusLabels[value] ?? value
  }

  // 优先级值映射
  if (field === 'priority') {
    const priorityLabels: Record<string, string> = {
      critical: '紧急',
      high: '高',
      medium: '中',
      low: '低',
    }
    return priorityLabels[value] ?? value
  }

  // 类型值映射
  if (field === 'type') {
    const typeLabels: Record<string, string> = {
      epic: '史诗',
      story: '故事',
      task: '任务',
      bug: '缺陷',
      subtask: '子任务',
    }
    return typeLabels[value] ?? value
  }

  return value
}

function getStageLabel(stage: string): string {
  const labels: Record<string, string> = {
    created: '已创建',
    product_discussion: '产品讨论',
    ui_design: 'UI 设计',
    ui_confirmation: 'UI 确认',
    frontend_development: '前端开发',
    backend_development: '后端开发',
    code_review: '代码审查',
    ops_deployment: '运维部署',
    testing: '测试',
    product_acceptance: '产品验收',
    done: '已完成',
  }
  return labels[stage] ?? stage
}

function getTrackLabel(track: string): string {
  const labels: Record<string, string> = {
    frontend: '前端轨道',
    backend: '后端轨道',
  }
  return labels[track] ?? track
}

/**
 * 格式化完整时间戳
 * 显示完整的系统时间：YYYY-MM-DD HH:mm:ss
 */
function formatFullTimestamp(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}
